import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Header() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { key: "nav.about", href: "/" },
    { key: "nav.yokohama", href: "/yokohama" },
    { key: "nav.pricing", href: "/pricing" },
    { key: "nav.firstTime", href: "/first-time" },
    { key: "nav.faq", href: "/faq" },
    { key: "nav.access", href: "/access" },
    { key: "nav.gift", href: "/gift" },
    { key: "nav.column", href: "/column" },
    { key: "nav.company", href: "/company" },
    { key: "nav.contact", href: "/contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isHeroPage = ["/", "/yokohama", "/pricing"].includes(location.pathname);
  const bgStyle = scrolled || !isHeroPage
    ? "bg-white/95 backdrop-blur-md shadow-sm"
    : "bg-transparent";
  const textStyle = scrolled || !isHeroPage
    ? "text-airdoa-navy"
    : "text-airdoa-navy [text-shadow:0_1px_3px_rgba(255,255,255,0.8)]";

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${bgStyle}`}>
        <div className="px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <img
                src="https://storage.readdy-site.link/project_files/c1f8e568-4952-4e6e-ba0d-4132aeaff1e6/b034907f-f499-43dd-a4b8-521068ddd260_AIRDOA.png?v=c470717b8f4d46fdaf3346483a6a693b"
                alt="AIRDOA"
                className="h-8 md:h-10 w-auto object-contain transition-all duration-300"
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`px-3 py-2 text-xs tracking-wide transition-colors duration-200 whitespace-nowrap ${textStyle} ${isActive ? "font-semibold" : "hover:text-airdoa-gold"}`}
                  >
                    {t(item.key)}
                  </Link>
                );
              })}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <LanguageSwitcher isDark={false} />
              <Link
                to="/yokohama"
                className={`text-xs px-5 py-2.5 rounded-md font-medium tracking-wide transition-all duration-300 whitespace-nowrap ${scrolled || !isHeroPage ? "bg-airdoa-gold text-airdoa-navy hover:bg-airdoa-gold-dark" : "bg-airdoa-gold text-airdoa-navy hover:bg-airdoa-gold-light"}`}
              >
                {t("nav.viewYokohama")}
              </Link>
            </div>

            <div className="flex lg:hidden items-center gap-2">
              <LanguageSwitcher isDark={false} />
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className={`w-10 h-10 flex items-center justify-center ${textStyle}`}
                aria-label="メニュー"
              >
                <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-xl`} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-airdoa-navy/98 backdrop-blur-sm lg:hidden">
          <div className="pt-24 px-6 pb-8 overflow-y-auto h-full">
            <nav className="flex flex-col gap-1">
              {navItems.map((item, idx) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`py-4 text-base text-white border-b border-white/10 transition-all duration-300 hover:text-airdoa-gold ${isActive ? "text-airdoa-gold font-semibold" : ""}`}
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    {t(item.key)}
                  </Link>
                );
              })}
            </nav>
            <div className="mt-8 flex flex-col gap-3">
              <Link to="/yokohama" className="btn-primary w-full text-center">
                {t("common.viewYokohama")}
              </Link>
              <Link to="/pricing" className="btn-secondary w-full text-center">
                {t("common.viewPricing")}
              </Link>
              <Link to="/gift" className="btn-secondary w-full text-center">
                {t("common.buyGift")}
              </Link>
              <Link to="/contact" className="btn-ghost w-full text-center">
                {t("common.inquire")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
